import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';

// Keep the browser's patience longer than the gateway's 30s finalize timer so a
// genuinely stalled decode surfaces the gateway's authoritative terminal error
// instead of a second, competing browser-side timeout.
const STT_FINALIZE_TIMEOUT_MS = 35_000;

// AudioWorklet code inlined as a Blob URL — Vite doesn't auto-detect
// audioWorklet.addModule() URLs the way it detects new Worker() patterns.
// The worklet is tiny (~30 lines) and has no imports, so inlining is clean.
const WORKLET_CODE = `
const TARGET_RATE = 16000;
const CHUNK_SIZE = 4096;

class SttPcmProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.buffer = new Float32Array(CHUNK_SIZE);
    this.bufferOffset = 0;
    this.ratio = sampleRate / TARGET_RATE;
    this.resampleAccumulator = 0;
    this.stopping = false;
    this.port.onmessage = (event) => {
      if (event.data?.type !== 'flush') return;
      this.stopping = true;
      const samples = this.buffer.slice(0, this.bufferOffset);
      this.bufferOffset = 0;
      this.port.postMessage({ type: 'flush-complete', samples });
    };
  }

  process(inputs) {
    if (this.stopping) return true;
    const input = inputs[0]?.[0];
    if (!input || input.length === 0) return true;

    for (let i = 0; i < input.length; i++) {
      this.resampleAccumulator += 1;
      if (this.resampleAccumulator >= this.ratio) {
        this.resampleAccumulator -= this.ratio;
        this.buffer[this.bufferOffset++] = input[i];

        if (this.bufferOffset >= CHUNK_SIZE) {
          this.port.postMessage(this.buffer.slice(0));
          this.bufferOffset = 0;
        }
      }
    }

    return true;
  }
}

registerProcessor('stt-pcm-processor', SttPcmProcessor);
`;

let workletBlobUrl: string | null = null;
function getWorkletUrl(): string {
  if (!workletBlobUrl) {
    const blob = new Blob([WORKLET_CODE], { type: 'application/javascript' });
    workletBlobUrl = URL.createObjectURL(blob);
  }
  return workletBlobUrl;
}

/** Prop bundle for threading streaming STT plumbing through the component tree.
 *  Created in App.tsx from useChat return values, passed unchanged to AppShell → ChatArea → ChatInput / VoiceZone. */
export interface SttStreamingProps {
  available: boolean;
  sendStart: (language?: string) => void;
  sendStop: () => void;
  sendBinary: (data: ArrayBuffer) => void;
  onSegmentRef: MutableRefObject<((text: string, completed: boolean) => void) | null>;
  onReadyRef: MutableRefObject<(() => void) | null>;
  onDoneRef: MutableRefObject<(() => void) | null>;
  onDisconnectRef: MutableRefObject<(() => void) | null>;
  onErrorRef: MutableRefObject<((message: string, fallback: boolean) => void) | null>;
}

export interface UseStreamingSTTOptions {
  /** Send the stt_stream_start message to the gateway */
  sendStreamStart: (language?: string) => void;
  /** Send the stt_stream_stop message to the gateway */
  sendStreamStop: () => void;
  /** Send a raw binary frame over the WS */
  sendBinaryFrame: (data: ArrayBuffer) => void;
  /** Ref wired by useChat for segment callbacks */
  onSegmentRef: MutableRefObject<((text: string, completed: boolean) => void) | null>;
  /** Ref wired by useChat for ready callback */
  onReadyRef: MutableRefObject<(() => void) | null>;
  /** Ref wired by useChat when Whisper has emitted every final segment */
  onDoneRef: MutableRefObject<(() => void) | null>;
  /** Ref wired by useChat when the owning WebSocket closes */
  onDisconnectRef: MutableRefObject<(() => void) | null>;
  /** Ref wired by useChat for error callback */
  onErrorRef: MutableRefObject<((message: string, fallback: boolean) => void) | null>;
}

export interface StreamingSTTState {
  /** Mic is open and sending audio frames */
  isStreaming: boolean;
  /** Capture stopped; waiting for Whisper's ordered final tail + done frame */
  isFinalizing: boolean;
  /** Accumulated finalized text (append-only — completed segments go here) */
  finalText: string;
  /** Current interim text (replaced on each non-completed segment) */
  interimText: string;
  /** Full transcript: finalText + interimText */
  fullTranscript: string;
  /** Start streaming */
  start: () => void;
  /** Stop streaming */
  stop: () => void;
  /** Toggle start/stop */
  toggle: () => void;
  /** Whether the browser supports AudioWorklet + getUserMedia */
  supported: boolean;
  /** Error message if something broke */
  error: string | null;
  /** Reset accumulated text (e.g. after sending a message) */
  reset: () => void;
}

export function useStreamingSTT(opts: UseStreamingSTTOptions): StreamingSTTState {
  const {
    sendStreamStart, sendStreamStop, sendBinaryFrame,
    onSegmentRef, onReadyRef, onDoneRef, onDisconnectRef, onErrorRef,
  } = opts;

  const [isStreaming, setIsStreaming] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [finalText, setFinalText] = useState('');
  const [interimText, setInterimText] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Refs for cleanup
  const audioContextRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const scriptNodeRef = useRef<any>(null); // ScriptProcessorNode (deprecated, no TS types)
  const silentGainRef = useRef<GainNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const flushScriptResidualRef = useRef<(() => void) | null>(null);
  const flushWorkletResidualRef = useRef<((done: (samples: Float32Array) => void) => void) | null>(null);
  const stopPendingRef = useRef(false);
  const stopFlushTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isStreamingRef = useRef(false);
  const isStartingRef = useRef(false);
  const isFinalizingRef = useRef(false);
  // Invalidates an async start() that outlives its owning WebSocket. Without
  // this epoch, a pending getUserMedia()/AudioWorklet await can resume after
  // disconnect and begin sending PCM through wsRef.current's replacement socket.
  const captureEpochRef = useRef(0);
  // Segments may arrive after stop() while Whisper flushes its final words.
  // reset() closes that acceptance window so a late segment from the previous
  // dictation cannot repaint a sent/cleared composer.  A new stream opens the
  // window only after the gateway acknowledges stt_stream_ready.
  const acceptsSegmentsRef = useRef(false);
  const finalTextRef = useRef('');
  const readyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const finalizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ScriptProcessorNode is deprecated but universally supported — use as fallback
  // when AudioWorkletNode isn't available (some Android browsers).
  const hasWorklet = typeof AudioWorkletNode !== 'undefined';
  const hasScriptProcessor = typeof AudioContext !== 'undefined'
    && typeof AudioContext.prototype.createScriptProcessor === 'function';

  const supported = typeof navigator !== 'undefined'
    && !!navigator.mediaDevices?.getUserMedia
    && typeof AudioContext !== 'undefined'
    && (hasWorklet || hasScriptProcessor);

  // ── Wire segment callback ──
  useEffect(() => {
    onSegmentRef.current = (text: string, completed: boolean) => {
      if (!acceptsSegmentsRef.current) return;
      if (completed) {
        setFinalText(prev => {
          const updated = prev ? `${prev} ${text.trim()}` : text.trim();
          finalTextRef.current = updated;
          return updated;
        });
        setInterimText('');
      } else {
        setInterimText(text.trim());
      }
    };
    return () => { onSegmentRef.current = null; };
  }, [onSegmentRef]);

  // ── Wire ready callback ──
  useEffect(() => {
    onReadyRef.current = () => {
      // Gateway confirmed streaming is ready — mic frames are being forwarded
      console.log('[streaming-stt] server acknowledged stream ready');
      acceptsSegmentsRef.current = true;
      if (readyTimeoutRef.current) {
        clearTimeout(readyTimeoutRef.current);
        readyTimeoutRef.current = null;
      }
    };
    return () => { onReadyRef.current = null; };
  }, [onReadyRef]);

  // ── Wire finalization callback ──
  useEffect(() => {
    onDoneRef.current = () => {
      if (!isFinalizingRef.current) return;
      acceptsSegmentsRef.current = false;
      isFinalizingRef.current = false;
      setIsFinalizing(false);
      if (finalizeTimeoutRef.current) {
        clearTimeout(finalizeTimeoutRef.current);
        finalizeTimeoutRef.current = null;
      }
    };
    return () => { onDoneRef.current = null; };
  }, [onDoneRef]);

  // A gateway stream belongs to one WebSocket. Stop capture immediately when
  // that socket dies so PCM can never spill into its replacement connection.
  useEffect(() => {
    onDisconnectRef.current = () => {
      if (!isStartingRef.current && !isStreamingRef.current && !isFinalizingRef.current) return;
      setError('Speech connection closed before finalization');
      cleanup();
    };
    return () => { onDisconnectRef.current = null; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onDisconnectRef]);

  // ── Wire error callback ──
  useEffect(() => {
    onErrorRef.current = (message: string, _fallback: boolean) => {
      setError(message);
      cleanup();
    };
    return () => { onErrorRef.current = null; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onErrorRef]);

  function stopCapture() {
    if (workletNodeRef.current) {
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }
    if (scriptNodeRef.current) {
      scriptNodeRef.current.disconnect();
      scriptNodeRef.current = null;
    }
    if (silentGainRef.current) {
      silentGainRef.current.disconnect();
      silentGainRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    flushScriptResidualRef.current = null;
    flushWorkletResidualRef.current = null;
  }

  function cleanup() {
    captureEpochRef.current++;
    isStartingRef.current = false;
    stopPendingRef.current = false;
    if (stopFlushTimeoutRef.current) {
      clearTimeout(stopFlushTimeoutRef.current);
      stopFlushTimeoutRef.current = null;
    }
    isStreamingRef.current = false;
    isFinalizingRef.current = false;
    acceptsSegmentsRef.current = false;
    setIsStreaming(false);
    setIsFinalizing(false);

    if (readyTimeoutRef.current) {
      clearTimeout(readyTimeoutRef.current);
      readyTimeoutRef.current = null;
    }
    if (finalizeTimeoutRef.current) {
      clearTimeout(finalizeTimeoutRef.current);
      finalizeTimeoutRef.current = null;
    }
    stopCapture();
  }

  const start = useCallback(async () => {
    if (isStreamingRef.current || isStartingRef.current || isFinalizingRef.current) return;
    const captureEpoch = ++captureEpochRef.current;
    isStartingRef.current = true;
    // Every mic press is a new dictation. ChatInput has already captured the
    // existing composer value as its prefix, so retaining this hook's previous
    // transcript would duplicate it on the next segment.
    acceptsSegmentsRef.current = false;
    setFinalText('');
    setInterimText('');
    finalTextRef.current = '';
    setError(null);
    console.log('[streaming-stt] start() called, hasWorklet:', hasWorklet);

    try {
      // 1. Get mic
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 },
      });
      if (captureEpoch !== captureEpochRef.current) {
        stream.getTracks().forEach(track => track.stop());
        return;
      }
      streamRef.current = stream;

      // 2. Create AudioContext — must resume for Android (autoplay policy)
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }
      if (captureEpoch !== captureEpochRef.current) {
        stopCapture();
        return;
      }
      console.log('[streaming-stt] AudioContext state:', audioContext.state, 'rate:', audioContext.sampleRate);

      const source = audioContext.createMediaStreamSource(stream);

      // 3. Downsample mic audio to 16kHz PCM and send binary frames
      //    Use AudioWorklet if available, ScriptProcessorNode as fallback
      let frameCount = 0;
      const nativeSampleRate = audioContext.sampleRate;
      const ratio = nativeSampleRate / 16000;

      let usedWorklet = false;

      if (hasWorklet) {
        try {
          // ── AudioWorklet path (preferred — runs off main thread) ──
          console.log('[streaming-stt] trying AudioWorklet');
          await audioContext.audioWorklet.addModule(getWorkletUrl());
          if (captureEpoch !== captureEpochRef.current) {
            stopCapture();
            return;
          }
          const workletNode = new AudioWorkletNode(audioContext, 'stt-pcm-processor');
          workletNodeRef.current = workletNode;

          let completeResidualFlush: ((samples: Float32Array) => void) | null = null;
          workletNode.port.onmessage = (e: MessageEvent<Float32Array | { type: 'flush-complete'; samples: Float32Array }>) => {
            if (!(e.data instanceof Float32Array)) {
              if (e.data.type !== 'flush-complete') return;
              const done = completeResidualFlush;
              completeResidualFlush = null;
              done?.(e.data.samples);
              return;
            }
            if (!isStreamingRef.current) return;
            frameCount++;
            if (frameCount <= 3 || frameCount % 100 === 0) {
              console.log(`[streaming-stt] frame #${frameCount}, ${e.data.length} samples`);
            }
            sendBinaryFrame(e.data.buffer as ArrayBuffer);
          };
          flushWorkletResidualRef.current = (done) => {
            completeResidualFlush = done;
            workletNode.port.postMessage({ type: 'flush' });
          };

          source.connect(workletNode);
          usedWorklet = true;
        } catch (workletErr) {
          console.warn('[streaming-stt] AudioWorklet failed, falling back to ScriptProcessor:', workletErr);
        }
      }

      if (!usedWorklet) {
        // ── ScriptProcessorNode fallback (deprecated but universally supported) ──
        if (!hasScriptProcessor) {
          throw new Error('Neither AudioWorklet nor ScriptProcessor available');
        }
        console.log('[streaming-stt] using ScriptProcessorNode fallback');
        const CHUNK_SIZE = 4096;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const scriptNode = (audioContext as any).createScriptProcessor(4096, 1, 1);
        scriptNodeRef.current = scriptNode;

        // Downsample buffer — same algorithm as the worklet
        const resampleBuf = new Float32Array(CHUNK_SIZE);
        let bufOffset = 0;
        let resampleAcc = 0;
        flushScriptResidualRef.current = () => {
          if (bufOffset === 0) return;
          sendBinaryFrame(resampleBuf.slice(0, bufOffset).buffer as ArrayBuffer);
          bufOffset = 0;
        };

        scriptNode.onaudioprocess = (e: AudioProcessingEvent) => {
          if (!isStreamingRef.current) return;
          const input = e.inputBuffer.getChannelData(0);

          for (let i = 0; i < input.length; i++) {
            resampleAcc += 1;
            if (resampleAcc >= ratio) {
              resampleAcc -= ratio;
              resampleBuf[bufOffset++] = input[i];
              if (bufOffset >= CHUNK_SIZE) {
                frameCount++;
                if (frameCount <= 3 || frameCount % 100 === 0) {
                  console.log(`[streaming-stt] frame #${frameCount}, ${CHUNK_SIZE} samples (ScriptProcessor)`);
                }
                sendBinaryFrame(resampleBuf.slice(0).buffer as ArrayBuffer);
                bufOffset = 0;
              }
            }
          }
        };

        source.connect(scriptNode);
        // ScriptProcessorNode requires connection to destination to fire
        // Route through silent gain to prevent mic playback through speakers
        const silentGain = audioContext.createGain();
        silentGain.gain.value = 0;
        scriptNode.connect(silentGain);
        silentGain.connect(audioContext.destination);
        silentGainRef.current = silentGain;
      }

      // 4. Tell gateway to start streaming
      if (captureEpoch !== captureEpochRef.current) {
        stopCapture();
        return;
      }
      const lang = navigator.language?.split('-')[0];
      console.log('[streaming-stt] sending stt_stream_start, lang:', lang);
      sendStreamStart(lang);

      isStreamingRef.current = true;
      isStartingRef.current = false;
      setIsStreaming(true);
      console.log('[streaming-stt] streaming active — waiting for server ready');

      // Ready-or-die: if the server doesn't acknowledge within 5s, the WS send
      // probably failed silently (connection dropped). Auto-cleanup so the button
      // doesn't stay stuck yellow forever.
      readyTimeoutRef.current = setTimeout(() => {
        if (isStreamingRef.current) {
          console.warn('[streaming-stt] server did not acknowledge within 5s — cleaning up');
          setError('Connection to speech server failed');
          cleanup();
        }
      }, 5000);
    } catch (err) {
      console.error('[streaming-stt] start failed:', err);
      setError(err instanceof Error ? err.message : 'Microphone access denied');
      cleanup();
    }
  }, [sendStreamStart, sendBinaryFrame, hasWorklet, hasScriptProcessor]);

  const finishStop = useCallback(() => {
    if (!stopPendingRef.current) return;
    if (stopFlushTimeoutRef.current) {
      clearTimeout(stopFlushTimeoutRef.current);
      stopFlushTimeoutRef.current = null;
    }
    // ScriptProcessor owns its residual buffer on the main thread, so flush it
    // synchronously before END_OF_AUDIO. AudioWorklet residual arrives before
    // this callback through its explicit flush-complete handshake.
    flushScriptResidualRef.current?.();
    sendStreamStop();
    stopPendingRef.current = false;
    isStreamingRef.current = false;
    setIsStreaming(false);
    isFinalizingRef.current = true;
    setIsFinalizing(true);
    if (readyTimeoutRef.current) {
      clearTimeout(readyTimeoutRef.current);
      readyTimeoutRef.current = null;
    }
    stopCapture();
    finalizeTimeoutRef.current = setTimeout(() => {
      if (!isFinalizingRef.current) return;
      setError('Speech finalization timed out');
      cleanup();
    }, STT_FINALIZE_TIMEOUT_MS);
  }, [sendStreamStop]);

  const stop = useCallback(() => {
    if (!isStreamingRef.current || stopPendingRef.current) return;
    stopPendingRef.current = true;
    const captureEpoch = captureEpochRef.current;
    const flushWorklet = flushWorkletResidualRef.current;
    if (flushWorklet) {
      // An AudioWorklet can disappear without acknowledging its message port.
      // Bound the wait; finishStop's stopPending gate makes timeout/ack exactly-once.
      stopFlushTimeoutRef.current = setTimeout(() => {
        if (!stopPendingRef.current || captureEpoch !== captureEpochRef.current) return;
        finishStop();
      }, 500);
      flushWorklet((samples) => {
        if (!stopPendingRef.current || captureEpoch !== captureEpochRef.current) return;
        if (samples.length > 0) sendBinaryFrame(samples.buffer as ArrayBuffer);
        finishStop();
      });
      return;
    }
    finishStop();
  }, [finishStop, sendBinaryFrame]);

  const toggle = useCallback(() => {
    if (isFinalizingRef.current) return;
    if (isStreamingRef.current) stop();
    else start();
  }, [start, stop]);

  const reset = useCallback(() => {
    if (isFinalizingRef.current) return;
    acceptsSegmentsRef.current = false;
    setFinalText('');
    setInterimText('');
    finalTextRef.current = '';
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isStreamingRef.current) {
        // Script residual is synchronously available: preserve it before END.
        // AudioWorklet teardown cannot await a port round-trip during unmount;
        // do not request a tail that could arrive after END or hang teardown.
        flushScriptResidualRef.current?.();
        sendStreamStop();
      }
      cleanup();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fullTranscript = interimText
    ? (finalText ? `${finalText} ${interimText}` : interimText)
    : finalText;

  return {
    isStreaming,
    isFinalizing,
    finalText,
    interimText,
    fullTranscript,
    start,
    stop,
    toggle,
    supported,
    error,
    reset,
  };
}
